const { jQuery: $ } = window;

/**
 * Renders a shortcode's summary pieces after every fetch: the applied-filter chips (one removable chip per
 * active selection) and the results-count readout. A service with a single `load(instance)` scoped to
 * `instance.$el`. Chips are built from the live controls (each adapter's `getSelectionLabels()`), so labels
 * always match what's rendered. Both pieces are opt-in (`useChips`/`useResultCount`) and no-op without their
 * container. Hooks: filter `chip`, filter `results_count_text`, action `applied_filters_rendered`.
 */
export default class AppliedFilters {
    /** @member {Hooks} */
    #hooks;

    /** @member {Logger} */
    #logger;

    /**
     * @param {Hooks} hooks
     * @param {Logger} logger
     */
    constructor(hooks, logger) {
        this.#hooks = hooks;
        this.#logger = logger;
    }

    /**
     * (Re)renders chips + results count for the instance. Safe to call repeatedly; called on init and after
     * every posts fetch.
     *
     * @param {ShortcodeInstance} instance
     */
    load(instance) {
        this.#renderChips(instance);
        this.#renderCount(instance);
        this.#announce(instance);
        this.#hooks.doAction("applied_filters_rendered", instance);
    }

    /**
     * Announces the result state to screen readers via the visually-hidden polite live region
     * (`.kpfsc__sr-status`). Runs after every fetch regardless of whether chips/count are enabled. Text is
     * filterable (`results_status_text`).
     *
     * @param {ShortcodeInstance} instance
     */
    #announce(instance) {
        const $status = instance.$el.find(".kpfsc__sr-status");
        if($status.length === 0) {
            return;
        }

        const n = this.#resultCount(instance);
        const text = this.#hooks.applyFilter(
            "results_status_text",
            n === 0 ? "No results found" : `${n} ${n === 1 ? "result" : "results"}`,
            n,
            instance,
        );

        $status.text(text);
    }

    /**
     * Rebuilds the `.kpfsc__chips` strip from the active selections across every adapter. Each chip carries
     * `data-filter` + `data-value` so the delegated remove handler can route back to the owning adapter.
     * Toggles `kpfsc--no-chips` on the form when nothing is active.
     *
     * @param {ShortcodeInstance} instance
     */
    #renderChips(instance) {
        const store = instance.store;
        const $chips = instance.$el.find(".kpfsc__chips");

        if(!store.get("useChips") || $chips.length === 0) {
            return;
        }

        $chips.empty();
        let count = 0;

        instance.adapters.forEach((adapter) => {
            if(typeof adapter.getSelectionLabels !== "function") {
                return;
            }

            const filterLabel = adapter.label;

            adapter.getSelectionLabels().forEach(({ value, label }) => {
                count++;
                $chips.append(this.#chip(instance, {
                    filter: adapter.name,
                    filterLabel,
                    value: String(value),
                    label,
                }));
            });
        });

        instance.$el.toggleClass("kpfsc--no-chips", count === 0);
        this.#bindRemoval(instance, $chips);
    }

    /**
     * Builds one chip. Default markup is assembled with jQuery so every user/term-derived string is set via
     * `.text()` / `.attr()` (never interpolated into HTML). Themes can replace it via the `chip` filter,
     * which receives the default element plus the chip's `{filter, filterLabel, value, label}` + instance.
     *
     * @returns {jQuery|string}
     */
    #chip(instance, data) {
        const $chip = $('<button type="button" class="kpfsc__chip" role="listitem"></button>')
            .attr("data-filter", data.filter)
            .attr("data-value", data.value)
            .attr("aria-label", `Remove filter ${data.filterLabel}: ${data.label}`);

        $chip.append($('<span class="kpfsc__chip-label"></span>').text(`${data.filterLabel}: ${data.label}`));
        $chip.append($('<span class="kpfsc__chip-remove" aria-hidden="true"></span>').text("×"));

        return this.#hooks.applyFilter("chip", $chip, data, instance);
    }

    /**
     * Binds a single delegated click handler for chip removal (idempotent — namespaced + re-bound each
     * render). Removing a chip drops just that value from its adapter, whose `fireChange` drives the standard
     * refetch → which re-runs load() and rebuilds the strip.
     *
     * @param {ShortcodeInstance} instance
     * @param {jQuery} $chips
     */
    #bindRemoval(instance, $chips) {
        $chips.off("click.kpfChip").on("click.kpfChip", ".kpfsc__chip", (e) => {
            e.preventDefault();

            const $chip = $(e.currentTarget);
            const filter = $chip.attr("data-filter");
            const value = $chip.attr("data-value");
            const adapter = instance.adapters.find((a) => a.name === filter);

            if(adapter && typeof adapter.removeValue === "function") {
                this.#logger.log("Removing filter chip:", filter, value);
                adapter.removeValue(value);

                // The removed chip's element is gone; keep keyboard focus in a stable place (the chips
                // strip persists — only its children rebuild) so screen-reader/keyboard users aren't dumped
                // to the top of the document.
                $chips.attr("tabindex", "-1").trigger("focus");
            }
        });
    }

    /**
     * Writes the current total into `.kpfsc__results-count`. The number comes from the last fetch's
     * queryMeta; the text is filterable (`results_count_text`) for localization / custom phrasing.
     *
     * @param {ShortcodeInstance} instance
     */
    #renderCount(instance) {
        const store = instance.store;
        const $count = instance.$el.find(".kpfsc__results-count");

        if(!store.get("useResultCount") || $count.length === 0) {
            return;
        }

        const n = this.#resultCount(instance);
        const text = this.#hooks.applyFilter("results_count_text", `${n} ${n === 1 ? "result" : "results"}`, n, instance);

        $count.text(text);
    }

    /**
     * The result total from the instance's last fetch (`queryMeta.posts`), or 0 when unavailable. Shared by
     * the count readout and the screen-reader announcement so both report the same number.
     *
     * @param {ShortcodeInstance} instance
     * @returns {number}
     */
    #resultCount(instance) {
        const meta = instance.store.get("queryMeta");
        return (meta && typeof meta.posts === "number") ? meta.posts : 0;
    }
}
